import type {ReactElement} from 'react';
import React from 'react';
import {ContextualMenuControlsOption} from '@Components/contextual-menu/components/contextual-menu-controls-option';
import {getCopiedStyles} from '@Components/poster-editor/poster-editor-reducer';
import {CopyStyleState, PosterItemControlOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-item-controls/poster-item-controls.types';
import type {BaseControlItem} from '@Components/contextual-menu/components/controls';
import styles from './poster-item-controls.module.scss';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useAppSelector} from '@/hooks';
import {onCopyStylesClicked, showCopyStylesAsNewFeature} from '@Components/poster-editor/library/poster-editor-library';

interface PosterItemControlsProps {
  onClickPosition(): void;
}

export function PosterItemControls({...props}: PosterItemControlsProps): ReactElement<any> {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const zIndex = useAppSelector((state) => {
    return state.posterEditor.posterObject?.pages.pagesHashMap[state.posterEditor.currentPageId]?.items.itemsOrder.indexOf(activeItemId);
  });
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();

  const copiedStylesState: CopyStyleState = useAppSelector(getCopiedStyles) ? CopyStyleState.COPIED : CopyStyleState.COPY;

  if (!activeItem) {
    throw new Error('Unable to show poster item controls as no item is selected');
  }

  const onBackControlClicked = (): void => {
    currentPage?.items.sendBackwardsSelectedItem();
  };
  const onDeleteControlClicked = (): void => {
    window.posterEditor?.whiteboard?.deleteItemById(activeItemId);
  };
  const onDuplicateControlClicked = (): void => {
    currentPage?.activeSelection.onDuplicate();
  };

  const onForwardControlClicked = (): void => {
    currentPage?.items.bringForwardSelectedItem();
  };

  const onClickHandler = (id: PosterItemControlOptions): void => {
    switch (id) {
      case PosterItemControlOptions.POSITION:
        props.onClickPosition();
        return;
      case PosterItemControlOptions.BACKWARD: {
        onBackControlClicked();
        return;
      }
      case PosterItemControlOptions.DELETE: {
        onDeleteControlClicked();
        return;
      }
      case PosterItemControlOptions.COPY_STYLES: {
        onCopyStylesClicked();
        return;
      }
      case PosterItemControlOptions.DUPLICATE: {
        onDuplicateControlClicked();
        return;
      }
      case PosterItemControlOptions.FORWARD: {
        onForwardControlClicked();
        return;
      }
      default:
        throw new Error('unhandled poster item control option!');
    }
  };

  const getData = (): BaseControlItem[] => {
    if (activeItem.isTranscript()) {
      return [
        {
          id: PosterItemControlOptions.POSITION,
          icon: 'icon-prints-stack',
          text: window.i18next.t('pmwjs_position'),
          className: styles.itemControl,
        },
        {
          id: PosterItemControlOptions.DUPLICATE,
          icon: 'icon-copy',
          text: window.i18next.t('pmwjs_duplicate'),
          className: `${styles.icon} ${styles.itemControl}`,
        },
        {
          id: PosterItemControlOptions.COPY_STYLES,
          icon: copiedStylesState === CopyStyleState.COPY ? 'icon-copy-style-brush' : 'icon-paste-style-brush',
          text: copiedStylesState === CopyStyleState.COPY ? window.i18next.t('pmwjs_copy_style') : window.i18next.t('pmwjs_style_copied'),
          isSelected: copiedStylesState !== CopyStyleState.COPY,
          className: styles.itemControl,
        },
        {
          id: PosterItemControlOptions.DELETE,
          icon: 'icon-delete',
          text: window.i18next.t('pmwjs_delete'),
          iconClasses: styles.deleteIcon,
          className: `${styles.icon} ${styles.itemControl}`,
        },
      ];
    }
    return [
      {
        id: PosterItemControlOptions.POSITION,
        icon: 'icon-prints-stack',
        text: window.i18next.t('pmwjs_position'),
        className: styles.itemControl,
      },
      {
        id: PosterItemControlOptions.DUPLICATE,
        icon: 'icon-copy',
        className: styles.itemControl,
        text: window.i18next.t('pmwjs_duplicate'),
      },
      {
        id: PosterItemControlOptions.COPY_STYLES,
        icon: copiedStylesState === CopyStyleState.COPY ? 'icon-copy-style-brush' : 'icon-paste-style-brush',
        text: copiedStylesState === CopyStyleState.COPY ? window.i18next.t('pmwjs_copy_style') : window.i18next.t('pmwjs_style_copied'),
        isSelected: copiedStylesState !== CopyStyleState.COPY,
        className: styles.itemControl,
        showAsNewFeature: showCopyStylesAsNewFeature(),
      },
      {
        id: PosterItemControlOptions.DELETE,
        icon: 'icon-delete',
        text: window.i18next.t('pmwjs_delete'),
        iconClasses: styles.deleteIcon,
        className: styles.itemControl,
      },
    ];
  };

  return (
    <ContextualMenuControlsOption
      key={zIndex}
      containerClassName={`spacing-m-b-2 ${activeItem.isTranscript() ? 'flex-center' : ''}`}
      itemsClassName="_full-width"
      id="poster-item-controls"
      onClick={onClickHandler}
      data={getData()}
    />
  );
}
